import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import { createPortal } from "react-dom";
import {
  CountrySelectorDropdown,
  usePhoneInput
} from "react-international-phone";
import "react-international-phone/style.css";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface Props {
  value: string;
  onChange: (phone: string) => void;
  label?: string;
  placeholder: string;
  inputWidth?: number | string;
  disabled?: boolean;
  size?: "medium" | "large";
}

export const CustomPhoneInput: React.FC<Props> = ({
  value,
  onChange,
  label,
  placeholder,
  inputWidth = 200,
  disabled = false,
  size = "medium"
}) => {
  const shouldFillInput = inputWidth === "100%";
  const inputId = useId();
  const phoneInput = usePhoneInput({
    value,
    defaultCountry: "us",
    // Show the dial code in the selector, not inside the input.
    // This prevents users from "editing" the country code by typing.
    disableDialCodeAndPrefix: true,
    onChange: ({ phone }) => onChange(phone)
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRootRef = useRef<HTMLDivElement | null>(null);

  const dropdownWidth = 420;
  const dropdownPos = useMemo(() => {
    const el = triggerRef.current;
    if (!el) return { left: 16, top: 0 };
    const rect = el.getBoundingClientRect();
    const gutter = 16;
    const left = Math.min(
      Math.max(rect.left, gutter),
      Math.max(gutter, window.innerWidth - gutter - dropdownWidth)
    );
    const top = rect.bottom + 10;
    return { left, top };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return () => {};
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsDropdownOpen(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (dropdownRootRef.current?.contains(t)) return;
      setIsDropdownOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, [isDropdownOpen]);

  const sharedStyleVars = useMemo(
    () =>
      ({
        // Country selector
        "--react-international-phone-country-selector-border-color":
          "var(--line-3)",
        "--react-international-phone-country-selector-background-color":
          "var(--elevated)",
        "--react-international-phone-country-selector-background-color-hover":
          "var(--fill-1)",

        // Dropdown
        "--react-international-phone-dropdown-item-height": "32px",
        "--react-international-phone-dropdown-item-font-size": "13px",
        "--react-international-phone-dropdown-item-background-color":
          "var(--elevated)",
        "--react-international-phone-selected-dropdown-item-background-color":
          "var(--fill-2)",
        "--react-international-phone-focused-dropdown-item-background-color":
          "var(--fill-1)",
        "--react-international-phone-text-color": "var(--fg)",
        "--react-international-phone-dropdown-shadow":
          "var(--shadow-popover-value)"
      } as React.CSSProperties),
    []
  );

  const content = (
    <div className="flex w-full items-center gap-2" style={sharedStyleVars}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setIsDropdownOpen((v) => !v)}
        className={cn(
          "flex shrink-0 items-center gap-1.5 rounded-md border border-line-3 bg-transparent px-2 text-base text-fg transition-colors duration-100 outline-none hover:bg-fill-1 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/40 active:bg-fill-1 disabled:cursor-default disabled:bg-fill-2",
          size === "large" ? "h-12" : "h-7"
        )}
      >
        <span className="whitespace-nowrap">
          +{String(phoneInput.country?.dialCode || "")}
        </span>
        <ChevronDown className="size-3.5 text-fg-3" />
      </button>

      {isDropdownOpen
        ? createPortal(
            <div
              ref={dropdownRootRef}
              style={{
                position: "fixed",
                left: dropdownPos.left,
                top: dropdownPos.top,
                width: dropdownWidth,
                maxWidth: "calc(100vw - 32px)",
                zIndex: 2147483647
              }}
            >
              <CountrySelectorDropdown
                show={true}
                selectedCountry={phoneInput.country.iso2}
                onSelect={(country) => {
                  phoneInput.setCountry(country.iso2, { focusOnInput: true });
                  setIsDropdownOpen(false);
                }}
                onClose={() => setIsDropdownOpen(false)}
                style={{
                  width: "100%",
                  maxHeight: 280,
                  overflow: "auto",
                  borderRadius: 8,
                  border: "1px solid var(--line-3)",
                  background: "var(--elevated)",
                  boxShadow: "var(--shadow-popover-value)",
                  padding: 4
                }}
                listItemStyle={{
                  paddingLeft: 8,
                  paddingRight: 8,
                  borderRadius: 6
                }}
                listItemFlagStyle={{
                  marginRight: 10,
                  width: 18,
                  height: 12,
                  borderRadius: 3
                }}
                listItemCountryNameStyle={{
                  fontSize: 13,
                  lineHeight: "18px",
                  color: "var(--fg)"
                }}
                listItemDialCodeStyle={{
                  fontSize: 13,
                  lineHeight: "18px",
                  color: "var(--fg-3)"
                }}
              />
            </div>,
            document.body
          )
        : null}

      <div
        className={shouldFillInput ? "min-w-0 flex-1" : "flex-none"}
        style={shouldFillInput ? undefined : { width: inputWidth }}
      >
        <Input
          id={inputId}
          placeholder={placeholder}
          type="tel"
          disabled={disabled}
          value={phoneInput.inputValue}
          onChange={phoneInput.handlePhoneValueChange}
          ref={phoneInput.inputRef}
          className={size === "large" ? "h-12 px-3" : undefined}
        />
      </div>
    </div>
  );

  return label ? (
    <div className="flex w-full flex-col items-start gap-1.5">
      <Label htmlFor={inputId}>{label}</Label>
      {content}
    </div>
  ) : (
    content
  );
};
